import { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Layers, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Shell } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Avatar, Flag, RedFlagIcon } from "../components/Avatar";
import { fmtRelativo, fmtFecha, tipoLabel } from "../lib/data";

const cityIcon = (n) => L.divIcon({ className: "", html: `<div style="width:38px;height:38px;border-radius:50%;background:#d4af37;color:#0b0c0e;font-weight:700;display:flex;align-items:center;justify-content:center;border:3px solid #0b0c0e;box-shadow:0 0 10px rgba(212,175,55,.6);font-family:Outfit,sans-serif">${n}</div>`, iconSize: [38, 38], iconAnchor: [19, 19] });
const placeIcon = (viaje) => L.divIcon({ className: "", html: `<div style="width:14px;height:14px;border-radius:50%;background:${viaje ? "#5b9bd5" : "#f1f5f9"};border:2px solid #0b0c0e"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] });

function Fit({ points, focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus) map.flyTo([focus.lat, focus.lng], 9);
    else if (points.length) map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 6, animate: false });
    return () => { try { map.stop(); } catch (e) { /* mapa ya destruido */ } };
  }, [points, focus, map]);
  return null;
}

const PersonRow = ({ p, extra }) => (
  <Link to={`/personas/${p.id}`} data-testid={`map-person-${p.id}`} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
    <Avatar nombre={p.nombre} src={p.foto_url} size={40} coronada={p.coronada} redFlag={p.red_flag} />
    <div className="flex-1 min-w-0"><p className="text-sm font-medium flex items-center gap-2">{p.nombre} <Flag iso={p.pais_iso} size={14} />{p.red_flag && <RedFlagIcon size={13} testId={`map-red-flag-${p.id}`} />}</p>
      <p className="text-xs text-muted-foreground">{extra || t("common.lastTime", { v: fmtRelativo(p.ultima_fisica) })}</p></div>
  </Link>
);

export default function Mapa() {
  const [ciudades, setCiudades] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [capa, setCapa] = useState(false);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/map/ciudades").then((r) => setCiudades(r.data)).catch(() => {});
    api.get("/map/lugares").then((r) => setLugares(r.data)).catch(() => {});
  }, []);
  const points = useMemo(() => [...ciudades, ...(capa ? lugares : [])], [ciudades, lugares, capa]);

  const buscar = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try { const { data } = await api.get("/map/buscar", { params: { ciudad: q.trim() } }); setRes(data); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Shell title={t("home.mapTitle")} fab={false}>
      <form onSubmit={buscar} className="relative mb-2">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="map-search-input" className="field pl-11 pr-24" placeholder={t("map.where")} value={q} onChange={(e) => setQ(e.target.value)} />
        <button data-testid="map-search-btn" disabled={busy} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-[#d4af37] text-[#0b0c0e] text-sm font-semibold px-4 py-2">{busy ? "…" : t("map.search")}</button>
      </form>
      <div className="relative rounded-2xl overflow-hidden border border-border" style={{ height: "calc(100dvh - 260px)", minHeight: 360 }} data-testid="map-container">
        <MapContainer center={[20, -20]} zoom={2} style={{ height: "100%", width: "100%", background: "#0b0c0e" }} zoomControl={false}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit points={points} focus={res} />
          {ciudades.map((c) => (
            <Marker key={c.ciudad} position={[c.lat, c.lng]} icon={cityIcon(c.count)} eventHandlers={{ click: () => setSel(c) }} />
          ))}
          {capa && lugares.map((l) => (
            <Marker key={l.id} position={[l.lat, l.lng]} icon={placeIcon(l.tipo === "viaje")}>
              <Popup><div style={{ fontSize: 12 }}><b>{l.nombre || l.ciudad}</b><br />{tipoLabel(l.tipo)} · {fmtFecha(l.fecha)}{l.fecha_fin ? ` → ${fmtFecha(l.fecha_fin)}` : ""}<br />{l.personas.map((p) => p.nombre).join(", ")}</div></Popup>
            </Marker>
          ))}
        </MapContainer>
        <button data-testid="map-layer-toggle" onClick={() => setCapa((c) => !c)} className={`absolute top-3 right-3 z-[500] chip bg-card ${capa ? "chip-on" : ""}`}><Layers size={14} /> {t("map.places")}</button>
        {ciudades.length === 0 && <div data-testid="map-empty" className="absolute inset-x-4 bottom-4 z-[500] surface p-3 text-center text-sm text-muted-foreground">{t("map.empty")}</div>}
      </div>

      <Sheet open={!!sel} onClose={() => setSel(null)} title={sel ? `${sel.ciudad}${sel.pais ? `, ${sel.pais}` : ""}` : ""} testId="map-city-sheet">
        {sel && <div>{sel.personas.map((p) => <PersonRow key={p.id} p={p} />)}</div>}
      </Sheet>

      <Sheet open={!!res} onClose={() => setRes(null)} title={res ? `${res.ciudad}` : ""} testId="map-search-sheet">
        {res && (
          <div className="space-y-4">
            {[[t("map.liveHere"), res.viven_aqui, (p) => t("common.lastTime", { v: fmtRelativo(p.ultima_fisica) })], [t("map.near"), res.cerca, (p) => `${p.ciudad} · a ${p.km} km`], [t("map.traveled"), res.viajaron_aqui, (p) => `${tipoLabel(p.tipo)} · ${fmtFecha(p.fecha)}${p.lugar ? ` · ${p.lugar}` : ""}`]].map(([t, list, ex]) => (
              <section key={t} data-testid={`search-section-${t.split(" ")[0].toLowerCase()}`}>
                <p className="label">{t}</p>
                {list.length === 0 ? <p className="text-sm text-muted-foreground">{t("map.nobody")}</p> : list.map((p, i) => <PersonRow key={p.id + i} p={p} extra={ex(p)} />)}
              </section>
            ))}
          </div>
        )}
      </Sheet>
    </Shell>
  );
}
